import type { DesignSite } from '@freedom/shared';
import { Antenna, Ellipsis, Minus, Plus, RadioTower, Trash2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import type { RadioLinkModel } from './radio-workspace';
import { displayEquipmentName } from './radio-parameters';

export interface SitePopupLink {
  link: RadioLinkModel;
  peer: DesignSite;
  distanceLabel: string;
  capacityLabel: string;
}

export function SitePopup({ site, position, links, nearbyCount, onSelectLink, onPatchSite, onCreatePtP, onCreatePtMp, onDelete }: {
  site: DesignSite;
  position: { x: number; y: number };
  links: SitePopupLink[];
  nearbyCount: number;
  onSelectLink(linkId: string): void;
  onPatchSite(patch: Partial<DesignSite>): void;
  onCreatePtP(): void;
  onCreatePtMp(): void;
  onDelete(): void;
}): React.JSX.Element {
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const [nameDraft, setNameDraft] = useState(site.name);

  useEffect(() => {
    setNameDraft(site.name);
  }, [site.id, site.name]);

  const commitName = (): void => {
    const nextName = nameDraft.trim();
    if (!nextName) {
      setNameDraft(site.name);
      return;
    }
    if (nextName !== site.name) onPatchSite({ name: nextName });
  };

  return <section className="site-popup" role="dialog" aria-label={`Объект ${site.name}`} style={{ left: position.x, top: position.y }} onMouseDown={(event) => event.stopPropagation()}>
    <header><span className="site-device-icon"><RadioTower size={17} /></span><strong>{site.name}</strong><button className={advancedOpen ? 'active' : ''} type="button" aria-label="Дополнительно" aria-expanded={advancedOpen} onClick={() => setAdvancedOpen((value) => !value)}><Ellipsis size={16} /></button></header>
    <div className="site-popup-table">
      <div className="site-popup-head"><span>PtP</span><span>Расстояние</span><span>Скорость</span></div>
      {links.length ? links.map(({ link, peer, distanceLabel, capacityLabel }) => <button type="button" key={link.id} onClick={() => onSelectLink(link.id)}><span><Antenna size={15} /><i><strong>{link.name}</strong><small>{peer.name} · {displayEquipmentName(peer.equipmentName)}</small></i></span><span>{distanceLabel}</span><span>{capacityLabel}</span></button>) : <p>Связанные радиолинки отсутствуют</p>}
    </div>
    {advancedOpen ? <div className="site-popup-advanced">
      <div className="site-popup-settings">
        <label className="site-popup-name"><span>Название</span><input aria-label="Название объекта" value={nameDraft} onChange={(event) => setNameDraft(event.target.value)} onBlur={commitName} onKeyDown={(event) => { if (event.key === 'Enter') event.currentTarget.blur(); if (event.key === 'Escape') { setNameDraft(site.name); event.currentTarget.blur(); } }} /></label>
        <label><span>Высота</span><b><button type="button" aria-label="Уменьшить высоту" onClick={() => onPatchSite({ antennaHeightM: Math.max(1, site.antennaHeightM - 1) })}><Minus size={11} /></button><strong>{site.antennaHeightM} м</strong><button type="button" aria-label="Увеличить высоту" onClick={() => onPatchSite({ antennaHeightM: site.antennaHeightM + 1 })}><Plus size={11} /></button></b></label>
        <label><span>Частота</span><b><input type="number" min="0.1" max="100" step="0.1" value={site.frequencyGHz} onChange={(event) => onPatchSite({ frequencyGHz: Number(event.target.value) })} /> ГГц</b></label>
      </div>
      <button className="site-popup-delete" type="button" onClick={onDelete}><Trash2 size={13} /> Удалить объект</button>
    </div> : null}
    <button className="nearby-row" type="button" disabled><span>Ближайшие устройства</span><strong>{nearbyCount}</strong></button>
    <footer><button type="button" onClick={onCreatePtMp}><RadioTower size={13} /> Добавить сектор</button><button type="button" onClick={onCreatePtP}><Antenna size={13} /> Создать PtP</button></footer>
  </section>;
}
